import React, { useEffect, useState } from "react";
import MaterialTable, { Column } from "@material-table/core";
import { Card, CardActions, CardContent, CardHeader } from "@mui/material";
import { Dispatch, SetStateAction } from "react";
import { MatterService } from "../../../services/ClientService";
import * as APIModels from "../../../models";
import { FileService } from "../../../services/SubmissionService";
import { Endpoints } from "../../../helpers/Endpoints";
import { Archive, Delete, Settings } from "@material-ui/icons";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { flatten, FlattenedMatterTemplate } from "../MatterManagmentTypes";
import { Merge } from "@mui/icons-material";
import { MatterLogResponseListDetailedResponse } from "../../../models/swagger/generated-client-service";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
dayjs.extend(timezone);

const MatterTemplateColumns: Column<FlattenedMatterTemplate>[] = [
    { title: "Filename", field: "filename", editable: "never" },
    { title: "Uploader", field: "uploader", editable: "never" },
    { title: "Notes", field: "notes" },
];

export default function TemplatesCard(params: {
    matterId: string;
    organizationId: string;
    userId: string;
    merge: (params: { body?: APIModels.MergeRequest }) => void;
    refreshMerged: () => void;
}) {
    const [templates, setTemplates] =
        useState<Array<FlattenedMatterTemplate>>();
    const [files, setFiles] = useState<
        {
            filename: string;
            size: number;
        }[]
    >([] as { filename: string; size: number }[]);
    const [note, setNote] = useState<string>("");

    const onDownload = (row: FlattenedMatterTemplate) => {
        FileService.get({
            submissionId: row.uploaderId + "/templates",
            documentId: row.filename ?? "",
            source: "upload",
        })
            .then((resp) => resp.json())
            .then((data) => {
                const downloadUrl = data.url;
                window.open(downloadUrl, "_blank");
            })
            .catch((error) => console.log(error));
    };

    useEffect(() => {
        MatterService.getAllTemplates({
            organizationId: params.organizationId,
        })
            .then((res) => {
                if (res.data && res.successful) {
                    setTemplates(
                        res.data?.map((template) => flatten(template))
                    );
                } else {
                    console.error({ 500: res.errorMessage });
                }
            })
            .catch((err) => {
                console.error({ Fatal: err });
            });
    }, [params.userId, params.organizationId, params.matterId]);

    const refreshData = () => {
        MatterService.getAllTemplates({
            organizationId: params.organizationId,
        })
            .then((res) => {
                if (res.data && res.successful) {
                    setTemplates(
                        res.data?.map((template) => flatten(template))
                    );
                } else {
                    console.error({ 500: res.errorMessage });
                }
            })
            .catch((err) => {
                console.error({ Fatal: err });
            });
    };

    const onFileUpload = (event: React.FormEvent) => {
        event.preventDefault();
        const input = event.target as HTMLInputElement;

        if (!input.files?.length) {
            return;
        }
        const filInfo = [...(files ?? [])];

        for (let i = 0; i < input.files.length; i++) {
            const f = input.files[i];

            filInfo.push({ filename: f.name, size: f.size });
            setFiles(filInfo);
            const filename = "templates/" + f.name;

            FileService.upload({
                fileServiceUrl: Endpoints.FileManager ?? "",
                file: f,
                filename: filename,
            })
                .then(
                    () => {
                        input.value = "";
                    },
                    (error) => {
                        input.value = "";
                        console.log(error);
                    }
                )
                .then(() => {
                    console.log({ user: params.userId });
                    MatterService.addTemplate({
                        body: {
                            userID: params.userId,
                            organizationID: params.organizationId,
                            filename: f.name,
                            notes: note,
                        } as APIModels.AddTemplateV2Request,
                    });
                })
                .then(refreshData);
        }
    };

    const createMergeParams = (row: FlattenedMatterTemplate) => {
        return {
            body: {
                matterID: params.matterId,
                uploaderId: row.uploaderId,
                filename: row.filename,
            },
        };
    };

    return (
        <Card
            sx={{
                margin: "12px 0",
            }}
        >
            <CardHeader>
                <h3>
                    <b>Templates</b>
                </h3>
            </CardHeader>
            <CardContent>
                <MaterialTable
                    columns={MatterTemplateColumns}
                    data={templates ?? []}
                    title="Templates"
                    editable={{
                        onRowUpdate: (newData, oldData) =>
                            new Promise((resolve, reject) => {
                                MatterService.updateTemplate({
                                    id: oldData?.id ?? "",
                                    body: {
                                        notes: newData.notes,
                                    },
                                }).then(
                                    () => {
                                        resolve(null);
                                        refreshData();
                                    },
                                    (error: unknown) => {
                                        console.log(error);
                                        resolve(null);
                                    }
                                );
                                resolve(null);
                            }),
                    }}
                    actions={[
                        {
                            icon: () => <Merge color="success" />,
                            tooltip: "Merge",
                            onClick: (event, rowData) => {
                                const row = rowData as FlattenedMatterTemplate;
                                params.merge(createMergeParams(row));
                                return params.refreshMerged();
                            },
                        },
                        {
                            icon: () => <FileDownloadIcon color="primary" />,
                            tooltip: "Download",
                            onClick: (event, rowData) => {
                                const row = rowData as FlattenedMatterTemplate;
                                const _ = onDownload(row);
                                return;
                            },
                        },
                        {
                            icon: () => <Delete color="error" />,
                            tooltip: "Archive",
                            onClick: (event, rowData) => {
                                const row = rowData as FlattenedMatterTemplate;
                                MatterService.deleteTemplate({
                                    templateId: row.id,
                                }).then(refreshData);
                            },
                        },
                        //{
                        //    icon: () => <Archive color="error" />,
                        //    tooltip: "Archive",
                        //    onClick: (event, rowData) => {
                        //        const row = rowData as FlattenedMatterTemplate;
                        //        MatterService.archiveTemplate({
                        //            templateId: row.id,
                        //        }).then(refreshData);
                        //    },
                        //},
                    ]}
                />
            </CardContent>
            <CardActions>
                <div className="fileupload btn btn-success waves-effect waves-light mb-3">
                    <input
                        type="file"
                        className="upload"
                        onChange={onFileUpload}
                        multiple
                    />
                    <span>Upload</span>
                </div>
            </CardActions>
        </Card>
    );
}
